const chunkSize = 50 * 1024 * 1024;
const retryDelays = [0, 1_000, 3_000, 5_000, 10_000];
const streamUploadDomains = ["videodelivery.net", "cloudflarestream.com"];

const uploadUrl = (value: string): string => {
  const url = new URL(value);
  const trustedHost = streamUploadDomains.some((domain) =>
    url.hostname === domain || url.hostname.endsWith(`.${domain}`),
  );
  if (
    url.protocol !== "https:" ||
    url.username !== "" ||
    url.password !== "" ||
    url.port !== "" ||
    !trustedHost
  ) {
    throw new Error("The video upload destination is invalid.");
  }
  return url.toString();
};

const offsetFrom = (response: Response, fallback?: number): number => {
  const raw = response.headers.get("Upload-Offset");
  if (raw === null && fallback !== undefined) return fallback;
  if (!raw || !/^\d+$/u.test(raw)) throw new Error("Cloudflare returned an invalid upload offset.");
  const offset = Number(raw);
  if (!Number.isSafeInteger(offset) || offset < 0) throw new Error("Cloudflare returned an invalid upload offset.");
  return offset;
};

const sleep = (milliseconds: number): Promise<void> => new Promise((resolve) => {
  globalThis.setTimeout(resolve, milliseconds);
});

const remoteOffset = async (url: string): Promise<number> => {
  const response = await fetch(url, { method: "HEAD", headers: { "Tus-Resumable": "1.0.0" } });
  if (!response.ok) throw new Error("The resumable upload could not be recovered.");
  return offsetFrom(response);
};

export const uploadVideoDirectly = async (
  file: File,
  destination: string,
  onProgress: (uploadedBytes: number, totalBytes: number) => void,
): Promise<void> => {
  const url = uploadUrl(destination);
  let offset = 0;
  onProgress(0, file.size);
  while (offset < file.size) {
    const end = Math.min(file.size, offset + chunkSize);
    let completed = false;
    for (const delay of retryDelays) {
      if (delay) await sleep(delay);
      try {
        const response = await fetch(url, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/offset+octet-stream",
            "Tus-Resumable": "1.0.0",
            "Upload-Offset": String(offset),
          },
          body: file.slice(offset, end),
        });
        if (!response.ok) throw new Error(`Video upload failed (${response.status}).`);
        const nextOffset = offsetFrom(response, end);
        if (nextOffset <= offset || nextOffset > file.size) throw new Error("Cloudflare returned an invalid upload offset.");
        offset = nextOffset;
        onProgress(offset, file.size);
        completed = true;
        break;
      } catch (error) {
        if (delay === retryDelays.at(-1)) throw error;
        try {
          offset = await remoteOffset(url);
          onProgress(offset, file.size);
          if (offset >= end) {
            completed = true;
            break;
          }
        } catch {
          // The next bounded retry rechecks the one-time upload endpoint.
        }
      }
    }
    if (!completed) throw new Error("The video upload could not be completed.");
  }
};
